import type { ReactNode } from 'react';
import { Outlet, createRootRoute, redirect } from '@tanstack/react-router';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import {
  AppShell,
  Burger,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';

import { AppSidebar } from '../layout/AppSidebar';
import { DataFreshness } from '../layout/DataFreshness';
import { LoginScreen } from '../layout/LoginScreen';
import { MeError, useMe } from '../auth/api';
import { msalInstance } from '../auth/msalConfig';

// Bramka dostępu: konto może być uwierzytelnione w Entra, ale niezaakceptowane
// (pending), zablokowane lub spoza domeny — backend zwraca wtedy 403 na /me.
// Zamiast wpuszczać do aplikacji (gdzie każde wywołanie i tak padnie), pokazujemy
// czytelny komunikat (FR-AUTH-02/05).
function AccessGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const { instance } = useMsal();

  if (me.isPending) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  if (me.isError) {
    const msg =
      me.error instanceof MeError ? me.error.message : 'Nie udało się pobrać danych konta.';
    return (
      <Center h="100vh" p="xl">
        <Stack align="center" gap="md" maw={520}>
          <Title order={3}>Brak dostępu</Title>
          <Text ta="center" c="dimmed">
            {msg}
          </Text>
          <Button variant="default" onClick={() => void instance.logoutRedirect()}>
            Wyloguj
          </Button>
        </Stack>
      </Center>
    );
  }
  return <>{children}</>;
}

function RootLayout() {
  const [open, setOpen] = useLocalStorage<boolean>({
    key: 'dzik:sidebar-open',
    defaultValue: true,
    getInitialValueInEffect: false,
  });

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginScreen />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AccessGate>
          <AppShell
            // Wysokość podyktowana banerem: wypełnia nagłówek na pełną
            // wysokość, bez marginesu nad i pod.
            header={{ height: 72 }}
            navbar={{
              width: 260,
              breakpoint: 0,
              collapsed: { desktop: !open, mobile: !open },
            }}
            padding="md"
          >
            <AppShell.Header>
              <Group h="100%" px="md" gap="sm" justify="space-between" wrap="nowrap">
                {/* `h="100%"` nie jest ozdobą: bez definitywnej wysokości rodzica
                    procentowa wysokość banera odnosi się do `auto`, a przeglądarka
                    rysuje wtedy obrazek w rozmiarze własnym (144 px). */}
                <Group gap="sm" wrap="nowrap" h="100%">
                  <Burger
                    opened={open}
                    onClick={() => setOpen((v) => !v)}
                    size="sm"
                    aria-label="Pokaż/ukryj menu"
                  />
                  {/* Baner niesie nazwę i rozwinięcie skrótu, więc `alt` musi je
                      powtórzyć — inaczej czytnik ekranu traci je razem z napisem,
                      który tu był. Plik jest w 2x (432x144) i NIE jest przycięty do
                      treści — logotyp ma własny przezroczysty margines, więc na
                      72 px paska sam znak zajmuje ok. 52 px, z prześwitem nad i pod.
                      Kadr jest celowo taki jak w źródle; przycięcie zmieniłoby
                      kompozycję znaku, a nie tylko jego rozmiar. */}
                  <img
                    src="/dzik-baner.png"
                    alt="DZiK — Do Zestawień i Kalkulacji"
                    // Wysokość wprost z wysokości paska (`--app-shell-header-height`,
                    // ustawianej przez `header={{ height }}`), więc jest jedno źródło
                    // prawdy. `maxHeight: 100%` ścina piksel zajęty przez dolną
                    // krawędź nagłówka — przy elemencie zastąpionym przeglądarka
                    // przelicza wtedy szerokość proporcjonalnie, więc proporcja
                    // banera zostaje zachowana.
                    style={{
                      height: 'var(--app-shell-header-height)',
                      maxHeight: '100%',
                      width: 'auto',
                      display: 'block',
                    }}
                  />
                </Group>
                <DataFreshness />
              </Group>
            </AppShell.Header>
            <AppShell.Navbar>
              <AppSidebar />
            </AppShell.Navbar>
            <AppShell.Main>
              <Outlet />
            </AppShell.Main>
          </AppShell>
        </AccessGate>
      </AuthenticatedTemplate>
    </>
  );
}

export const Route = createRootRoute({
  beforeLoad: ({ location }) => {
    const signedIn = msalInstance.getAllAccounts().length > 0;
    if (!signedIn && location.pathname !== '/') {
      throw redirect({ to: '/' });
    }
  },
  component: RootLayout,
});
